import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";
import { DIVISIONS, MASTER_LOGO } from "@/data/divisions";
import { COMPANY } from "@/data/company";

const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      data-testid={HOME.footer}
      className="relative bg-black text-white pt-24 pb-10 px-6 md:px-10 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1 }}
          className="font-editorial italic text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] max-w-5xl mb-6"
        >
          {t.footer.cta}
        </motion.h2>
        <Link
          to="/contact"
          className="inline-block font-display uppercase text-5xl md:text-7xl lg:text-8xl text-white ink-underline"
          style={{ "--accent": "#ec4899" }}
        >
          {t.footer.cta2}
        </Link>

        {/* Contact + divisions row */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          {/* Logo column */}
          <div className="md:col-span-4">
            <img
              src={MASTER_LOGO}
              alt="2TProd Studios"
              className="h-32 md:h-44 lg:h-56 w-auto"
            />
          </div>

          {/* Address column (no email) */}
          <div className="md:col-span-3">
            <div className="text-[0.6rem] tracking-hair uppercase text-white/40 mb-4">
              Studio
            </div>
            <address
              data-testid="footer-address"
              className="not-italic text-[0.85rem] leading-relaxed text-white/80"
            >
              <div className="text-white font-medium mb-1">{COMPANY.name}</div>
              <div>{COMPANY.address1}</div>
              <div>{COMPANY.address2}</div>
              <div className="mt-3">
                <a
                  href={COMPANY.phoneHref}
                  data-testid="footer-phone"
                  className="hover:text-white transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </div>
            </address>
          </div>

          {/* Divisions column */}
          <div className="md:col-span-3">
            <div className="text-[0.6rem] tracking-hair uppercase text-white/40 mb-4">
              Divisions
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-[0.85rem] text-white/75">
              {DIVISIONS.map((d) => (
                <a
                  key={d.slug}
                  href={`#chapter-${d.slug}`}
                  className="hover:text-white transition-colors"
                >
                  {d.name}
                </a>
              ))}
            </div>
          </div>

          {/* Studio + Follow column */}
          <div className="md:col-span-2">
            <div className="text-[0.6rem] tracking-hair uppercase text-white/40 mb-4">
              Studio
            </div>
            <div className="flex flex-col gap-2 text-[0.85rem] text-white/75 mb-6">
              <Link to="/" className="hover:text-white transition-colors">
                Index
              </Link>
              <Link
                to="/contact"
                className="hover:text-white transition-colors"
              >
                Contact
              </Link>
              <a
                href={COMPANY.mapLink}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white transition-colors"
              >
                Open in Maps ↗
              </a>
            </div>
            <div className="text-[0.6rem] tracking-hair uppercase text-white/40 mb-3">
              Follow
            </div>
            <div className="flex flex-col gap-2 text-[0.85rem] text-white/75">
              {COMPANY.socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {s.label} ↗
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between text-[0.65rem] tracking-hair uppercase text-white/45 gap-2">
          <span>{t.footer.copyright}</span>
          <span>Studios in motion — since day one</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
